import { signOut } from "firebase/auth";
import React, { useEffect, useState, useCallback } from "react"; // Added useCallback
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import auth from "../../firebase.init";
import { Bell, PackagePlus, ShoppingBag, User, UserPlus } from "lucide-react";
import TopCategoriesNav from "./TopCategoriesNav";
import { CategoriesNavItem, MobileCategoriesNavItem } from "./CategoriesModal";
import { useSecureFetch } from "../../hooks/useSecureFetch";
import NotificationBell from "./NotificationBell";

const NavBar = () => {
  const { secureFetch } = useSecureFetch(); // Destructure secureFetch from the hook
  const [logo, setLogo] = useState({});
  const [user] = useAuthState(auth);
  const [userInfo, setUserInfo] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const handleSignout = () => {
    signOut(auth)
      .then(() => {
        navigate("/");
      })
      .catch((error) => {
        console.error("Logout error:", error);
      });
  };

  const [searchValue, setSearchValue] = useState("");
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(
        `/search-products/?query=${encodeURIComponent(searchValue.trim())}`
      );
    }
  };

  // Fetch Logo
  useEffect(() => {
    fetch(`https://server.enjoywiki.com/flip-server/logo`)
      .then((res) => res.json())
      .then((info) => setLogo(info[0]));
  }, []);

  // Fetch User Info
  const fetchUserInfo = useCallback(async () => {
    try {
      const data = await secureFetch(
        `https://server.enjoywiki.com/flip-server/user-data?UserEmail=${user?.email}`
      );
      setUserInfo(data);
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  }, [user?.email, secureFetch]); // Depend on user.email and secureFetch

  useEffect(() => {
    if (user?.email) {
      fetchUserInfo();
    } else {
      setUserInfo([]); // Clear user info if not logged in
    }
  }, [user?.email, fetchUserInfo]);

  // Fetch All Products
  const fetchAllProducts = useCallback(async () => {
    try {
      const info = await secureFetch(
        `https://server.enjoywiki.com/flip-server/products`
      );
      // Assuming products endpoint returns an array
      if (Array.isArray(info)) {
        setProducts(info.reverse());
      } else {
        console.warn("Products API did not return an array:", info);
        setProducts([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, [secureFetch]);

  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  // Fetch Categories
  const fetchCategories = useCallback(async () => {
    try {
      const info = await secureFetch(
        `https://server.enjoywiki.com/flip-server/categories`
      );
      // Assuming categories endpoint returns an array
      if (Array.isArray(info)) {
        setCategories(info);
      } else {
        console.warn("Categories API did not return an array:", info);
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [secureFetch]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // This useEffect is redundant with the one above it, keeping it for now, but usually it would be removed
  // useEffect(() => {
  //     fetch(`https://server.enjoywiki.com/flip-server/categories`)
  //         .then((res) => res.json())
  //         .then((info) => setCategories(info));
  // }, []);

  const uniqueCategories = [
    ...new Set(categories.map((category) => JSON.stringify(category))),
  ].map((item) => JSON.parse(item));

  const [hideUserDashLink, setHideUserDashLink] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setHideUserDashLink(window.innerWidth >= 992);
    };
    // Initial check on component mount
    handleResize();
    // Add event listener for window resize
    window.addEventListener("resize", handleResize);
    // Cleanup on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const defaultProfileImage =
    "https://static.vecteezy.com/system/resources/previews/002/002/403/non_2x/man-with-beard-avatar-character-isolated-icon-free-vector.jpg";

  return (
    <>
      <header className="header-primary">
        <div className="container">
          <nav className="navbar navbar-expand-xl justify-content-between">
            <Link to="/">
              {/* Display logo if fetched, otherwise fallback or loading */}
              <img
                src={
                  logo.logoUrl || "/assets/img/logo/FlipBundle_main_Logo.png"
                }
                width={200}
                alt="FlipBundle Logo"
              />
            </Link>
            <div className="collapse navbar-collapse" id="navbarNav">
              <form className="col-12 m-lg-4" onSubmit={handleSearch}>
                <div
                  className="hero-form-wrapper bg-white d-flex position-relative"
                  style={{ height: 60 }}
                >
                  <div>
                    <input
                      type="text"
                      className="form-control shadow-none"
                      placeholder="Search for any product..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                    />
                    <button
                      type="submit"
                      className="hero-form-btn position-absolute"
                      style={{ padding: "14px 20px" }}
                    >
                      <svg
                        width={19}
                        height={18}
                        viewBox="0 0 19 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z"
                          stroke="#F2F2F2"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M18 17L14 13"
                          stroke="#F2F2F2"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </form>
              <ul>
                <li className="d-block d-xl-none mt-4">
                  <a
                    href="/products"
                    style={{
                      color: "#22be0d",
                      backgroundColor: "#ffffff00",
                      padding: "10px 22px",
                    }}
                  >
                    <ShoppingBag />
                    All Products
                  </a>
                </li>

                <li className="nav-item mt-2">
                  <div className="d-block d-lg-none">
                    <MobileCategoriesNavItem categories={uniqueCategories} />
                  </div>
                </li>
              </ul>
              <div className="d-flex align-items-center gap-4 mt-4">
                <div className="d-flex d-lg-none">
                  {user ? (
                    <>
                      {/* Ensure userInfo is an array before mapping */}
                      {Array.isArray(userInfo) &&
                        userInfo
                          .filter((profile) => profile.UserEmail === user.email)
                          .map((profile) => (
                            <div
                              key={profile.UserEmail}
                              className="d-flex flex-column gap-2"
                            >
                              {profile.userRole === "Seller" && (
                                <a
                                  href="/seller/dashboard"
                                  className="header-btn"
                                >
                                  <User /> Seller Dashboard
                                </a>
                              )}
                              {profile.userRole === "Buyer" && (
                                <a
                                  href="/buyer/dashboard"
                                  className="header-btn"
                                >
                                  <User /> Buyer Dashboard
                                </a>
                              )}
                              {profile.userRole === "Admin" && (
                                <a
                                  href="/admin/dashboard"
                                  className="header-btn"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <User /> Admin Dashboard
                                </a>
                              )}
                              <button
                                onClick={handleSignout}
                                className="header-btn"
                              >
                                <UserPlus /> Logout
                              </button>
                            </div>
                          ))}
                    </>
                  ) : (
                    <Link to="/login" className="header-btn">
                      <User /> Login
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* <NotificationBell /> */}
            <div className="navbar-right d-flex align-items-center gap-4">
              <div className="align-items-center d-none d-lg-flex">
                <div className="d-flex">
                  <Link
                    to="/products"
                    className="w-btn-secondary-lg"
                    style={{
                      color: "#22be0d",
                      backgroundColor: "#ffffff00",
                      padding: "10px 22px",
                    }}
                  >
                    <ShoppingBag />
                    All Products
                  </Link>
                </div>

                {user ? (
                  <>
                    <div className="dropdown">
                      <button
                        className="dropdown-toggle custom-dropdown-btn"
                        id="dropdownMenuButton"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                      >
                        {/* Ensure userInfo is an array before mapping */}
                        {Array.isArray(userInfo) &&
                          userInfo
                            .filter(
                              (profile) => profile.UserEmail === user.email
                            )
                            .map((profile) => (
                              <img
                                key={profile.UserEmail} // Added key here
                                src={
                                  profile.profileURL
                                    ? profile.profileURL
                                    : defaultProfileImage
                                }
                                width={56}
                                height={56}
                                alt="Profile" // Changed alt text
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                }}
                              />
                            ))}
                      </button>
                      <div
                        className="dropdown-menu"
                        aria-labelledby="dropdownMenuButton"
                      >
                        {user &&
                          user.email &&
                          Array.isArray(userInfo) &&
                          userInfo.length > 0 && (
                            <>
                              {userInfo.map((profile) => {
                                if (profile.UserEmail === user?.email) {
                                  return (
                                    <React.Fragment key={profile.UserEmail}>
                                      {" "}
                                      {/* Added React.Fragment and key */}
                                      {profile.userRole === "Seller" && (
                                        <a
                                          className="dropdown-item text-black"
                                          href="/seller/dashboard"
                                          style={{ color: "black" }}
                                        >
                                          Dashboard
                                        </a>
                                      )}
                                      {profile.userRole === "Seller" && (
                                        <Link
                                          className="dropdown-item text-black"
                                          to="/sell-now"
                                          style={{ color: "black" }}
                                        >
                                          Sell Now
                                        </Link>
                                      )}
                                      {profile.userRole === "Buyer" && (
                                        <a
                                          className="dropdown-item text-black"
                                          href="/buyer/dashboard"
                                          style={{ color: "black" }}
                                        >
                                          <span>Dashboard</span>
                                        </a>
                                      )}
                                      {profile.userRole === "Admin" && (
                                        <a
                                          className="dropdown-item text-black"
                                          target="_blank"
                                          rel="noreferrer"
                                          href="/admin/dashboard"
                                          style={{ color: "black" }}
                                        >
                                          <span>Admin Dashboard</span>
                                        </a>
                                      )}
                                    </React.Fragment>
                                  );
                                }
                                return null;
                              })}
                            </>
                          )}
                        <div
                          className="dropdown-item"
                          onClick={handleSignout}
                          style={{ color: "black", cursor: "pointer" }}
                        >
                          Logout
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="d-flex">
                    <Link
                      to="/login"
                      className="w-btn-secondary-lg"
                      style={{
                        color: "#22be0d",
                        backgroundColor: "#ffffff00",
                        padding: "10px 22px",
                      }}
                    >
                      <User />
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="w-btn-secondary-lg"
                      style={{
                        color: "#22be0d",
                        backgroundColor: "#ffffff00",
                        padding: "10px 22px",
                      }}
                    >
                      <UserPlus />
                      Signup
                    </Link>
                    {/* This block was duplicated and needs to be outside the login/register condition */}
                    {/* For unauthenticated users, this "Sell Now" link will always be visible regardless of user role */}
                    <div className="d-none d-lg-block">
                      <Link to="/sell-now" className="w-btn-secondary-lg">
                        <PackagePlus />
                        Sell Now
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              <button
                className="navbar-toggler d-block d-xl-none"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#navbarNav"
                aria-controls="navbarNav"
                aria-expanded="false"
                aria-label="Toggle navigation"
              >
                <span />
              </button>
            </div>
          </nav>
        </div>
      </header>
      <TopCategoriesNav />
      <>
        {/* Category Modal */}
        <div
          className="modal fade categor"
          id="exampleModal"
          tabIndex={-1}
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header px-5 py-4 d-flex justify-content-between items-placeholder border-bottom">
                <div>
                  <h3 className="text-dark-300 fw-bold text-24">
                    All Categories
                  </h3>
                </div>
                <div>
                  <button
                    type="button"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <svg
                      width={32}
                      height={32}
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx={16} cy={16} r={16} fill="#FF3838" />
                      <path
                        d="M22.2188 9.77759L8.88614 23.1109"
                        stroke="white"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22.2188 23.1099L8.88614 9.77654"
                        stroke="white"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="modal-body px-5 py-4">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="row">
                      <div className="col-lg-3 mb-4">
                        <div>
                          <h4 className="text-18 fw-semibold text-dark-300 mb-2">
                            Graphic
                          </h4>
                          {/* Ensure uniqueCategories is an array */}
                          {Array.isArray(uniqueCategories) &&
                            uniqueCategories.map(
                              (category) =>
                                category.mainCategory === "Graphic" && (
                                  <React.Fragment key={category.slug}>
                                    <nav className="category-nav">
                                      <ul>
                                        <li>
                                          <a
                                            href={`/category/${category.slug}`}
                                          >
                                            {category.categoryName}
                                          </a>
                                        </li>
                                      </ul>
                                    </nav>
                                  </React.Fragment>
                                )
                            )}
                        </div>
                      </div>
                      <div className="col-lg-3 mb-4">
                        <div>
                          <h4 className="text-18 fw-semibold text-dark-300 mb-2">
                            Templates
                          </h4>
                          {Array.isArray(uniqueCategories) &&
                            uniqueCategories.map(
                              (category) =>
                                category.mainCategory === "Template" && (
                                  <React.Fragment key={category.slug}>
                                    <nav className="category-nav">
                                      <ul>
                                        <li>
                                          <a
                                            href={`/category/${category.slug}`}
                                          >
                                            {category.categoryName}
                                          </a>
                                        </li>
                                      </ul>
                                    </nav>
                                  </React.Fragment>
                                )
                            )}
                        </div>
                      </div>
                      <div className="col-lg-3 mb-4">
                        <div>
                          <h4 className="text-18 fw-semibold text-dark-300 mb-2">
                            Database
                          </h4>
                          {Array.isArray(uniqueCategories) &&
                            uniqueCategories.map(
                              (category) =>
                                category.mainCategory === "Database" && (
                                  <React.Fragment key={category.slug}>
                                    <nav className="category-nav">
                                      <ul>
                                        <li>
                                          <a
                                            href={`/category/${category.slug}`}
                                          >
                                            {category.categoryName}
                                          </a>
                                        </li>
                                      </ul>
                                    </nav>
                                  </React.Fragment>
                                )
                            )}
                        </div>
                      </div>
                      <div className="col-lg-3 mb-4 mb-lg-0">
                        <div>
                          <h4 className="text-18 fw-semibold text-dark-300 mb-2">
                            Website
                          </h4>
                          {Array.isArray(uniqueCategories) &&
                            uniqueCategories.map(
                              (category) =>
                                category.mainCategory === "Website" && (
                                  <React.Fragment key={category.slug}>
                                    <nav className="category-nav">
                                      <ul>
                                        <li>
                                          <a
                                            href={`/category/${category.slug}`}
                                          >
                                            {category.categoryName}
                                          </a>
                                        </li>
                                      </ul>
                                    </nav>
                                  </React.Fragment>
                                )
                            )}
                        </div>
                      </div>
                      <div className="col-lg-3 mb-4 mb-lg-0">
                        <div>
                          <h4 className="text-18 fw-semibold text-dark-300 mb-2">
                            E-Book
                          </h4>
                          {Array.isArray(uniqueCategories) &&
                            uniqueCategories.map(
                              (category) =>
                                category.mainCategory === "eBook" && (
                                  <React.Fragment key={category.slug}>
                                    <nav className="category-nav">
                                      <ul>
                                        <li>
                                          <a
                                            href={`/category/${category.slug}`}
                                          >
                                            {category.categoryName}
                                          </a>
                                        </li>
                                      </ul>
                                    </nav>
                                  </React.Fragment>
                                )
                            )}
                        </div>
                      </div>
                      <div className="col-lg-3 mb-4 mb-lg-0">
                        <div>
                          <h4 className="text-18 fw-semibold text-dark-300 mb-2">
                            Photos
                          </h4>
                          {Array.isArray(uniqueCategories) &&
                            uniqueCategories.map(
                              (category) =>
                                category.mainCategory === "StockPhotos" && (
                                  <React.Fragment key={category.slug}>
                                    <nav className="category-nav">
                                      <ul>
                                        <li>
                                          <a
                                            href={`/category/${category.slug}`}
                                          >
                                            {category.categoryName}
                                          </a>
                                        </li>
                                      </ul>
                                    </nav>
                                  </React.Fragment>
                                )
                            )}
                        </div>
                      </div>
                      <div className="col-lg-3 mb-4 mb-lg-0">
                        <div>
                          <h4 className="text-18 fw-semibold text-dark-300 mb-2">
                            Videos
                          </h4>
                          {Array.isArray(uniqueCategories) &&
                            uniqueCategories.map(
                              (category) =>
                                category.mainCategory === "StockVideos" && (
                                  <React.Fragment key={category.slug}>
                                    <nav className="category-nav">
                                      <ul>
                                        <li>
                                          <a
                                            href={`/category/${category.slug}`}
                                          >
                                            {category.categoryName}
                                          </a>
                                        </li>
                                      </ul>
                                    </nav>
                                  </React.Fragment>
                                )
                            )}
                        </div>
                      </div>
                      <div className="col-lg-3 mb-4 mb-lg-0">
                        <div>
                          <h4 className="text-18 fw-semibold text-dark-300 mb-2">
                            Software
                          </h4>
                          {Array.isArray(uniqueCategories) &&
                            uniqueCategories.map(
                              (category) =>
                                category.mainCategory === "Software" && (
                                  <React.Fragment key={category.slug}>
                                    <nav className="category-nav">
                                      <ul>
                                        <li>
                                          <a
                                            href={`/category/${category.slug}`}
                                          >
                                            {category.categoryName}
                                          </a>
                                        </li>
                                      </ul>
                                    </nav>
                                  </React.Fragment>
                                )
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Modal End */}
      </>
    </>
  );
};

export default NavBar;
